import React, { useState } from "react";
import Auth from "../../service/Auth";
import { useAuth } from "../../providers/AuthContext";
import { API_URL } from "../../helpers/constants";
import { FaEye, FaEyeSlash, FaLock, FaUserCircle } from "react-icons/fa";
import { FaSpinner } from "react-icons/fa";

const RegisterForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const [profil, setProfil] = useState({
    username: "",
    password: "",
    role: "Select Role",
  });
  const [message, setMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(""); // success or error
  const [usernameError, setUsernameError] = useState(""); // New state for username validation error
  const [passwordError, setPasswordError] = useState(""); // New state for password validation error
  const auth = new Auth(API_URL);
  const { user } = useAuth();

  const hierarchy = ["Owner", "Partner", "SuperAgent", "Agent", "User"];
  const userRoleIndex = hierarchy.indexOf(user?.role);
  const filteredHierarchy = hierarchy.filter((role) => hierarchy.indexOf(role) > userRoleIndex);

  const handleRegister = async () => {
    if (!user || !user._id) {
      setMessage("User ID is not available. Please try again.");
      setModalType("error");
      setIsModalOpen(true);
      return;
    }

    try {
      // Check if there are any validation errors before submitting
      if (usernameError || passwordError) {
        setMessage(usernameError || passwordError);
        setModalType("error");
        setIsModalOpen(true);
        return;
      }

      // Additional server-side validation
      if (!profil.username) {
        setMessage("Username is required.");
        setModalType("error");
        setIsModalOpen(true);
        return;
      }

      // Validate role selection
      if (profil.role === "Select Role") {
        setMessage("Please select a role.");
        setModalType("error");
        setIsModalOpen(true);
        return;
      }

      setIsLoading(true); // Set loading state to true

      const updatedProfil = {
        ...profil,
        id: user._id, // ID of the currently logged-in user (creator)
      };

      const response = await auth.registerUser(updatedProfil);

      if (response.success && response.status === 201) {
        // Successful registration
        setMessage(response.message || "User registered successfully!");
        setModalType("success");
        handleReset();
      } else {
        // Handle backend error responses
        setMessage(response.message || "Error occurred during registration.");
        setModalType("error");
      }

      setIsModalOpen(true); // Show the modal
    } catch (error) {
      console.error("Error during user registration:", error); // Debug log
      setMessage("Error registering user. Please try again.");
      setModalType("error");
      setIsModalOpen(true); // Show the modal
    } finally {
      setIsLoading(false); // Set loading state to false
    }
  };

  const validateUsername = (value) => {
    const alphanumericRegex = /^[a-zA-Z0-9]*$/;
    if (!value) {
      setUsernameError("Username is required");
      return false;
    } else if (!alphanumericRegex.test(value)) {
      setUsernameError("Only letters and numbers allowed. No spaces or special characters.");
      return false;
    } else if (value.length < 4) {
      setUsernameError("Username must be at least 4 characters long");
      return false;
    } else if (value.length > 16) {
      setUsernameError("Username cannot exceed 16 characters");
      return false;
    } else {
      setUsernameError("");
      return true;
    }
  };

  const validatePassword = (value) => {
    if (!value) {
      setPasswordError("Password is required");
      return false;
    } else if (/\s/.test(value)) {
      setPasswordError("Password cannot contain spaces");
      return false;
    } else if (value.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return false;
    } else {
      setPasswordError("");
      return true;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "username") {
      // For username, validate and only update if it contains valid characters
      const alphanumericRegex = /^[a-zA-Z0-9]*$/;
      if (value === "" || alphanumericRegex.test(value)) {
        setProfil((prev) => ({
          ...prev,
          [name]: value.toLowerCase(),
        }));
        // Validate the username after updating state
        validateUsername(value.toLowerCase());
      }
      // If invalid characters, don't update state (prevents typing special chars)
    } else if (name === "password") {
      // For password, don't allow spaces to be entered
      if (value === "" || !/\s/.test(value)) {
        setProfil((prev) => ({
          ...prev,
          [name]: value,
        }));
        validatePassword(value);
      }
    } else {
      setProfil((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleReset = () => {
    setProfil({ username: "", password: "", role: "Select Role" });
    setUsernameError("");
    setPasswordError("");
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="w-full sm:p-1">
      <header className="rounded-lg bg-[#474747] text-white w-full p-3">
        <h1 className="md:text-2xl text-lg font-bold md:text-left text-center">Register User</h1>
      </header>
      <div className="w-full max-w-4xl rounded-lg">
        <div className="w-full pt-5 max-w-lg">
          <form>
            {/* Username Input */}
            <div className="relative">
              <label className="block pb-1 text-md text-[#242424]">Username</label>
              <div className="relative">
                {/* Icon */}
                <span className="absolute inset-y-0 left-0 pt-2 pl-4">
                  <FaUserCircle className="text-primary-dark" size={20} />
                </span>
                {/* Input Field */}
                <input
                  className={`w-full text-gray-700 border rounded-lg pl-12 p-1.5 focus:ring-0 focus:ring-white ${
                    usernameError ? "border-red-500" : ""
                  }`}
                  type="text"
                  name="username"
                  placeholder="Username (letters and numbers only)"
                  value={profil.username}
                  onChange={(e) => handleChange(e)}
                  onBlur={(e) => validateUsername(e.target.value)}
                />
              </div>
              {/* Username validation error message */}
              {usernameError && <div className="text-red-500 text-sm mt-1">{usernameError}</div>}
            </div>
            <div className="relative">
              <label className="block pb-1 text-md text-[#242424] mt-2">Password</label>
              <div className="relative">
                {/* Input Field */}

                <span className="absolute inset-y-0 left-0 pt-2.5 pl-5">
                  <FaLock className="text-primary-dark" size={15} />
                </span>
                {/* Toggle Icon */}
                <input
                  type={showPassword ? "text" : "password"}
                  className={`border text-gray-700 border-n rounded-lg bg-left appearance-none  -lg p-2 pl-12 text-sm focus:ring-0 focus:ring-white w-full mb-2 ${
                    passwordError ? "border-red-500" : ""
                  }`}
                  value={profil.password}
                  name="password"
                  onChange={handleChange}
                  onBlur={(e) => validatePassword(e.target.value)}
                  required
                  autoComplete="off"
                />
                <span onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0  pt-2 right-3">
                  {showPassword ? <FaEye className="text-primary-dark" size={24} /> : <FaEyeSlash className="text-primary-dark" size={24} />}
                </span>
              </div>
              {/* Password validation error message */}
              {passwordError && <div className="text-red-500 text-sm mt-1">{passwordError}</div>}
            </div>
            {/* Role Dropdown */}
            <div className="mb-6">
              <label className="block pb-1 text-md text-[#242424]">Role</label>
              <div className="relative">
                <select
                  className="block appearance-none w-full bg-white border focus:ring-0 focus:ring-white border-black rounded p-2 text-gray-700 leading-tight focus:outline-none  pr-10"
                  name="role"
                  value={profil.role}
                  onChange={handleChange}
                >
                  <option value="Select Role">Select Role</option>
                  {filteredHierarchy.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Register and Reset Buttons */}
            <div className="flex flex-col space-y-2">
              <button
                className={`bg-primary hover:bg-primary/80 text-black text-sm font-bold p-3.5 rounded-lg w-full focus:outline-none ${
                  usernameError || passwordError || profil.role === "Select Role" || isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                type="button"
                onClick={handleRegister}
                disabled={usernameError || passwordError || profil.role === "Select Role" || isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <FaSpinner className="animate-spin mr-2" size={20} />
                    Submitting...
                  </div>
                ) : (
                  "REGISTER USER"
                )}
              </button>
              <button
                className="border border-black hover:bg-gray-300 text-black font-bold p-3 rounded-lg w-full focus:outline-none"
                type="button"
                onClick={handleReset}
              >
                RESET
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal for showing messages */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-5 rounded shadow-lg max-w-xs w-full">
            <h2 className={`text-xl font-bold ${modalType === "success" ? "text-green-500" : "text-red-500"}`}>
              {modalType === "success" ? "Success" : "Error"}
            </h2>
            <p className="text-black">{message}</p>
            <button onClick={closeModal} className="mt-4 bg-primary/80 text-black py-2 px-4 rounded w-full hover:bg-primary/60">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterForm;
